import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { listClients } from "../../api/endpoints/clients"
import { listTickets } from "../../api/endpoints/tickets"
import { listUsers } from "../../api/endpoints/users"
import {
  getUnreadCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationsRead,
  type AdminNotificationRow,
} from "../../api/endpoints/notifications"
import { useToast } from "../../stores/toast"
import { getAdminNotificationPresentation } from "../../utils/notifications"
import { BellIcon, MenuIcon, SearchIcon, XMarkIcon } from "../icons"
import { NotificationItem } from "./notification_item"

const roleDashboardTitles: Record<string, string> = {
  SUPER_ADMIN: "Super Admin Dashboard",
  ADMIN: "Admin Dashboard",
  OPS_MANAGER: "Ops Manager Dashboard",
  AGENT: "Agent Dashboard",
  VERIFICATION_STAFF: "Verification Dashboard",
}

const routeTitles: Record<string, string> = {
  "/clients": "Clients / Portfolio",
  "/collections": "Collections",
  "/agent-performance": "Agent Performance",
  "/reconciliation": "Reconciliation",
  "/users": "Users & Roles",
  "/targets": "Targets & Assignments",
  "/disbursements": "Disbursements",
  "/reports": "Reports",
  "/tickets": "Tickets",
  "/notifications": "Notifications",
  "/system-config": "System Configuration",
  "/system-health": "System Health",
  "/audit-logs": "Audit Logs",
  "/verification": "Verification Queue",
  "/completed-reviews": "Completed Reviews",
  "/agent/pipeline": "My Pipeline",
  "/agent/clients": "My Clients",
  "/agent/interactions": "Interaction Log",
  "/agent/performance": "My Performance",
  "/borrower-applications": "Borrower Applications",
}

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
  if (seconds < 60) return "just now"
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export function TopBar({
  user,
  onMenuToggle,
  mobileNavigationOpen = false,
}: {
  user: { fullName: string; role: string; canReviewKyc?: boolean }
  onMenuToggle?: () => void
  mobileNavigationOpen?: boolean
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const title =
    location.pathname === "/dashboard"
      ? (roleDashboardTitles[user.role] ?? "Dashboard")
      : location.pathname === "/verification"
        ? user.canReviewKyc
          ? "Verification Queue"
          : "Verification / KYC"
        : (routeTitles[location.pathname] ?? "Dashboard")

  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [globalSearch, setGlobalSearch] = useState("")
  const [searchOpen, setSearchOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLDivElement>(null)
  const canSearchUsers = ["SUPER_ADMIN", "ADMIN"].includes(user.role)
  const searchTerm = globalSearch.trim()

  useEffect(() => {
    setSearchOpen(false)
    setDropdownOpen(false)
  }, [location.pathname])

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setSearchOpen(false)
        setDropdownOpen(false)
      }
    }

    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [])

  const { addToast } = useToast()
  const prevUnreadRef = useRef<number | null>(null)

  const unreadQuery = useQuery({
    queryKey: ["unread-count"],
    queryFn: () => getUnreadCount(),
    refetchInterval: 15_000,
  })

  const recentQuery = useQuery({
    queryKey: ["notifications-recent"],
    queryFn: () => listNotifications({ per_page: "10" }),
    enabled: dropdownOpen,
  })

  const markReadMutation = useMutation({
    mutationFn: (ids: string[]) => markNotificationsRead(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["unread-count"] })
      queryClient.invalidateQueries({ queryKey: ["notifications-recent"] })
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
    },
  })

  const markAllMutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["unread-count"] })
      queryClient.invalidateQueries({ queryKey: ["notifications-recent"] })
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
    },
  })

  const unreadCount = unreadQuery.data?.data?.unreadCount ?? 0
  const recentItems = recentQuery.data?.data ?? []

  useEffect(() => {
    if (!unreadQuery.isSuccess) return
    const prev = prevUnreadRef.current
    prevUnreadRef.current = unreadCount

    if (prev === null || unreadCount <= prev) return

    const newCount = unreadCount - prev
    listNotifications({ per_page: String(newCount), is_read: "false" })
      .then((response) => {
        const items = response?.data ?? []
        for (const item of items) {
          const presentation = getAdminNotificationPresentation(item)
          const resourceNav = presentation.target
            ? { label: `${presentation.target.label} →`, path: presentation.target.path }
            : undefined

          addToast({
            title: item.title,
            body: item.body,
            variant: "info",
            action: resourceNav,
          })
        }
      })
      .catch(() => {
      })
  }, [addToast, unreadCount, unreadQuery.isSuccess])

  const clientsQuery = useQuery({
    queryKey: ["topbar-search", "clients", searchTerm, user.role],
    queryFn: () => listClients({ search: searchTerm, per_page: "5" }),
    enabled: searchOpen && searchTerm.length >= 2,
  })

  const ticketsQuery = useQuery({
    queryKey: ["topbar-search", "tickets", searchTerm, user.role],
    queryFn: () => listTickets({ search: searchTerm, per_page: "5" }),
    enabled: searchOpen && searchTerm.length >= 2,
  })

  const usersQuery = useQuery({
    queryKey: ["topbar-search", "users", searchTerm, user.role],
    queryFn: () => listUsers({ search: searchTerm, per_page: "5" }),
    enabled: canSearchUsers && searchOpen && searchTerm.length >= 2,
  })

  const clientResults = clientsQuery.data?.data ?? []
  const ticketResults = ticketsQuery.data?.data ?? []
  const userResults = usersQuery.data?.data ?? []

  function handleSearchNavigate(path: string) {
    setSearchOpen(false)
    setGlobalSearch("")
    navigate(path)
  }

  const searchResults = (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
      {searchTerm.length < 2 ? (
        <p className="px-4 py-3 text-sm text-slate-500">
          Type at least 2 characters to search Admin-visible records.
        </p>
      ) : clientsQuery.isLoading || ticketsQuery.isLoading || usersQuery.isLoading ? (
        <p className="px-4 py-3 text-sm text-slate-500">Searching...</p>
      ) : clientResults.length === 0 && ticketResults.length === 0 && userResults.length === 0 ? (
        <p className="px-4 py-3 text-sm text-slate-500">No matches found.</p>
      ) : (
        <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
          {clientResults.length > 0 ? (
            <div className="px-2 py-2">
              <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Clients
              </p>
              {clientResults.map((entry: any) => (
                <button
                  key={entry.clientId}
                  type="button"
                  className="w-full rounded-md px-2 py-2 text-left hover:bg-slate-50"
                  onClick={() =>
                    handleSearchNavigate(`/clients?selected_client_id=${entry.clientId}`)
                  }
                >
                  <p className="text-sm font-medium text-slate-900">{entry.fullName}</p>
                  <p className="text-xs text-slate-500">
                    {entry.clientId} · {entry.phoneNumber}
                  </p>
                </button>
              ))}
            </div>
          ) : null}
          {canSearchUsers && userResults.length > 0 ? (
            <div className="px-2 py-2">
              <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Users
              </p>
              {userResults.map((entry: any) => (
                <button
                  key={entry.userId}
                  type="button"
                  className="w-full rounded-md px-2 py-2 text-left hover:bg-slate-50"
                  onClick={() => handleSearchNavigate(`/users?selected_user_id=${entry.userId}`)}
                >
                  <p className="text-sm font-medium text-slate-900">{entry.fullName}</p>
                  <p className="text-xs text-slate-500">
                    {entry.userId} · {entry.role}
                  </p>
                </button>
              ))}
            </div>
          ) : null}
          {ticketResults.length > 0 ? (
            <div className="px-2 py-2">
              <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Tickets
              </p>
              {ticketResults.map((entry: any) => (
                <button
                  key={entry.ticketId}
                  type="button"
                  className="w-full rounded-md px-2 py-2 text-left hover:bg-slate-50"
                  onClick={() => handleSearchNavigate(`/tickets?selected_ticket_id=${entry.ticketId}`)}
                >
                  <p className="text-sm font-medium text-slate-900">{entry.subject}</p>
                  <p className="text-xs text-slate-500">
                    {entry.ticketId} · {entry.status}
                  </p>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )

  return (
    <header className="sticky top-0 z-30 shrink-0 border-b border-slate-200/80 bg-white shadow-sm supports-[backdrop-filter]:bg-white/85 supports-[backdrop-filter]:backdrop-blur">
      <div className="flex min-h-16 items-start justify-between gap-3 px-4 py-3 sm:px-6">
        <div className="flex min-w-0 items-start gap-3">
          {onMenuToggle ? (
            <button
              type="button"
              aria-label="Open navigation menu"
              aria-expanded={mobileNavigationOpen}
              className="rounded-xl border border-slate-200 p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 lg:hidden"
              onClick={onMenuToggle}
            >
              <MenuIcon className="h-5 w-5" />
            </button>
          ) : null}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-base font-semibold text-slate-800 sm:text-lg">{title}</h1>
              <span className="hidden rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-medium text-emerald-800 sm:inline-flex sm:items-center">
                <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500" />
                Development
              </span>
            </div>
            <p className="mt-1 hidden text-xs text-slate-500 sm:block">
              Signed in as {user.role.replace(/_/g, " ")}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="relative hidden w-72 lg:block" ref={searchRef}>
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <SearchIcon className="h-4 w-4 text-slate-400" />
            </span>
            <input
              className="block w-full rounded-md border border-slate-200 bg-slate-50 py-1.5 pl-10 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
              placeholder={
                canSearchUsers ? "Search clients, users, tickets..." : "Search clients and tickets..."
              }
              type="text"
              value={globalSearch}
              onFocus={() => setSearchOpen(true)}
              onChange={(event) => {
                setGlobalSearch(event.target.value)
                setSearchOpen(true)
              }}
            />
            {searchOpen ? (
              <>
                <button
                  type="button"
                  className="fixed inset-0 z-30 cursor-default border-0 bg-transparent"
                  aria-label="Close search"
                  onClick={() => setSearchOpen(false)}
                />
                <div className="absolute left-0 right-0 top-full z-40 mt-2">{searchResults}</div>
              </>
            ) : null}
          </div>

          <button
            type="button"
            aria-label="Open search"
            className="rounded-xl border border-slate-200 p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 lg:hidden"
            onClick={() => setSearchOpen(true)}
          >
            <SearchIcon className="h-5 w-5" />
          </button>

          <div className="relative" ref={dropdownRef}>
            <button
              className={`relative rounded-xl border p-2 transition-colors ${
                dropdownOpen
                  ? "border-blue-200 bg-blue-50 text-blue-700"
                  : "border-transparent text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              }`}
              type="button"
              aria-label="Notifications"
              onClick={() => setDropdownOpen((current) => !current)}
            >
              <BellIcon className="h-6 w-6" />
              {unreadCount > 0 ? (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white ring-2 ring-white">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              ) : null}
            </button>

            {dropdownOpen ? (
              <>
                <button
                  className="fixed inset-0 z-40 cursor-default border-0 bg-transparent"
                  onClick={() => setDropdownOpen(false)}
                  type="button"
                  aria-label="Close notifications"
                />
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-label="Notifications panel"
                  className="fixed inset-x-3 bottom-3 top-[calc(env(safe-area-inset-top,0px)+4.75rem)] z-50 flex flex-col overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-2xl sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:bottom-auto sm:mt-2 sm:w-[min(24rem,calc(100vw-1rem))] sm:rounded-2xl"
                >
                  <div className="border-b border-slate-100 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 px-4 py-4 text-white">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-blue-100/90">
                          Notifications
                        </p>
                        <p className="mt-1 text-sm font-semibold text-white">
                          {unreadCount > 0
                            ? `${unreadCount} unread update${unreadCount === 1 ? "" : "s"}`
                            : "All caught up"}
                        </p>
                        <p className="mt-1 text-xs text-blue-100/80">
                          Review recent alerts, tickets, and client events without leaving the current screen.
                        </p>
                      </div>
                      <button
                        type="button"
                        aria-label="Close notifications panel"
                        className="rounded-full border border-white/15 bg-white/10 p-2 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-white/90">
                        {recentItems.length} in panel
                      </span>
                      {unreadCount > 0 ? (
                        <button
                          className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-900 transition-colors hover:bg-blue-50"
                          type="button"
                          onClick={() => markAllMutation.mutate()}
                        >
                          Mark all read
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto bg-slate-50/70 p-3 sm:max-h-80">
                    {recentQuery.isLoading ? (
                      <div className="space-y-3">
                        <div className="h-24 animate-pulse rounded-2xl border border-slate-200 bg-white" />
                        <div className="h-24 animate-pulse rounded-2xl border border-slate-200 bg-white" />
                        <div className="h-24 animate-pulse rounded-2xl border border-slate-200 bg-white" />
                      </div>
                    ) : recentQuery.isError ? (
                      <div className="flex h-full min-h-48 items-center justify-center rounded-2xl border border-red-200 bg-red-50 p-5 text-center">
                        <div>
                          <p className="text-sm font-semibold text-red-700">Unable to load notifications</p>
                          <p className="mt-1 text-xs text-red-600">
                            Refresh the panel or check the current session.
                          </p>
                        </div>
                      </div>
                    ) : recentItems.length === 0 ? (
                      <div className="flex h-full min-h-48 items-center justify-center rounded-2xl border border-slate-200 bg-white p-5 text-center">
                        <div>
                          <p className="text-sm font-semibold text-slate-800">No recent notifications</p>
                          <p className="mt-1 text-xs text-slate-500">
                            New activity will appear here as it arrives.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {recentItems.map((item: AdminNotificationRow) => {
                          const presentation = getAdminNotificationPresentation(item)
                          const target = presentation.target

                          return (
                            <NotificationItem
                              key={item.notificationId}
                              compact
                              body={item.body}
                              categoryLabel={presentation.categoryLabel}
                              eventLabel={presentation.eventLabel}
                              eventTone={presentation.eventTone}
                              isRead={item.isRead}
                              targetLabel={target?.label}
                              timestampLabel={timeAgo(item.createdAt)}
                              title={item.title}
                              onMarkRead={
                                !item.isRead ? () => markReadMutation.mutate([item.notificationId]) : undefined
                              }
                              onOpen={
                                target
                                  ? () => {
                                      if (!item.isRead) {
                                        markReadMutation.mutate([item.notificationId])
                                      }
                                      setDropdownOpen(false)
                                      navigate(target.path)
                                    }
                                  : undefined
                              }
                            />
                          )
                        })}
                      </div>
                    )}
                  </div>
                  <div className="border-t border-slate-100 bg-white px-4 py-3">
                    <button
                      type="button"
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-center text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100"
                      onClick={() => {
                        setDropdownOpen(false)
                        navigate("/notifications")
                      }}
                    >
                      Open notifications inbox
                    </button>
                  </div>
                </div>
              </>
            ) : null}
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden text-right xl:block">
              <p className="text-sm font-semibold text-slate-700">{user.fullName}</p>
              <p className="text-[0.65rem] font-bold uppercase tracking-widest text-slate-500">
                {user.role.replace(/_/g, " ")}
              </p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 bg-slate-200 text-xs font-bold text-slate-600">
              {user.fullName
                .split(" ")
                .map((name) => name[0])
                .join("")
                .slice(0, 2)
                .toUpperCase()}
            </div>
          </div>
        </div>
      </div>

      {searchOpen ? (
        <div className="border-t border-slate-100 bg-white px-4 py-4 lg:hidden">
          <div className="mx-auto max-w-3xl space-y-3">
            <div className="flex items-start gap-2">
              <div className="relative flex-1">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <SearchIcon className="h-4 w-4 text-slate-400" />
                </span>
                <input
                  autoFocus
                  className="block w-full rounded-md border border-slate-200 bg-slate-50 py-2 pl-10 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                  placeholder={
                    canSearchUsers ? "Search clients, users, tickets..." : "Search clients and tickets..."
                  }
                  type="text"
                  value={globalSearch}
                  onChange={(event) => setGlobalSearch(event.target.value)}
                />
              </div>
              <button
                type="button"
                aria-label="Close search"
                className="rounded-xl border border-slate-200 p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
                onClick={() => setSearchOpen(false)}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            {searchResults}
          </div>
        </div>
      ) : null}
    </header>
  )
}
